import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import cn from 'classnames';

import filterActiveStylists from 'bl/data/filterActiveStylists.bl';
import Button from 'components/form/Button.component';
import TextInput from 'components/form/TextInput.component';
import ToggleSwitch from 'components/form/ToggleSwitch.component';
import TypedOption from 'components/form/TypedOption.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import TruncatedText from 'components/generic/TruncatedText.component';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import { useCheckmap } from 'hooks/useCheckmap.hook';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import alen from 'utils/arrays/alen.util';
import { firstOf } from 'utils/arrays/elements.util';
import fromAnyToLowerCase from 'utils/converters/any/fromAnyToLowerCase.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import isNotFullArray from 'utils/predicates/array/isNotFullArray.util';
import equalStrings from 'utils/strings/equalStrings.util';

import CN from './KpiSummaryStaffSelector.module.scss';

const L = logger('KpiSummaryStaffSelector');

const determineStaffButtonText = (options) => {
  const { selectedName, isAllStaff, selectedCount, isUnselected, isMobile, t } = options ?? {};

  if (isUnselected && isMobile) return t('staffKpisReports.kpiReports.totalBusiness');
  if (isUnselected) return t('staffKpisReports.kpiReports.staffSelect.selectStaffMembers');

  if (1 === selectedCount) return selectedName;
  if (isAllStaff) return t('staffKpisReports.kpiReports.staffSelect.allStaffMembers');
  if (selectedCount) return t('staffKpisReports.kpiReports.staffSelect.countStaffMembers', { count: selectedCount });

  return t('staffKpisReports.kpiReports.staffSelect.selectStaffMembers');
};

const determineSelectedName = (options) => {
  const { ids, stylists } = options ?? {};

  const firstId = firstOf(ids);
  const firstStylist = stylists.find((stylist) => equalStrings(stylist?.id, firstId));

  return fromAnyToTrimmed(firstStylist?.userName);
};

const KpiSummaryStaffSelector = ({
  stylists: stylistsFromProps,
  clearOnDeselect,
  displaySelectAll,
  isLoading,
  selected,
  onApply, // no alwaysNoop for this one since we usually want to use it, so the console will complain if not there
  onCancel = alwaysNoop,
  onClose = alwaysNoop,
  onOpen = alwaysNoop,
  onTotalBusiness = alwaysNoop,
}) => {
  const isSelected = fromAnyToBool(selected);
  const isUnselected = !selected;

  const { t } = useTranslation();

  const activeStylists = filterActiveStylists(stylistsFromProps);

  const componentRef = useRef(null);
  const { isDarkMode } = useTheme();
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth <= MOBILE_VIEWPORT;

  const {
    val: stylists,
    set: setStylists,
    tip: toggleStylist,
    every: makeEveryStylist,
    everyFalse: deselectAllStylists,
    tKeys: ids,
  } = useCheckmap(activeStylists.map((s) => s?.id));

  const selectedCount = alen(ids);
  const allStylistsCount = alen(activeStylists);

  const [previousStylists, setPreviousStylists] = useState(stylists);

  const { val: isOpen, nay: closeStaff, tip: toggleStaff } = useTip(false);

  useEffect(() => {
    // only if asked by parent
    if (!clearOnDeselect) return;

    // once this control is deselected, clear any selection and previous selection
    if (isSelected) return;

    // don't update if open as well, let the user pick what they want
    if (isOpen) return;

    // if nothing is selected, no sense in updating it again
    if (!selectedCount) return;

    deselectAllStylists();
    setPreviousStylists([]);
  }, [isUnselected, deselectAllStylists, isSelected, selectedCount, isOpen, clearOnDeselect]);

  const [searchText, setSearchText] = useState('');

  const handleSelectStylist = useCallback((ev) => toggleStylist(ev?.id), [toggleStylist]);
  const handleSearch = useCallback((value) => void setSearchText(value?.string), [setSearchText]);

  const loweredSearchText = fromAnyToLowerCase(searchText);
  const searchPredicate = (stylist) => fromAnyToLowerCase(stylist.userName).includes(loweredSearchText);
  const filteredStylists = loweredSearchText ? activeStylists.filter(searchPredicate) : activeStylists;

  const hasSelectedStylists = fromAnyToBool(selectedCount);
  const hasNoSelectedStylists = !selectedCount;
  const isAllStaff = allStylistsCount === selectedCount;
  const isNotAllStaff = !isAllStaff;
  const hasAnySelectedStylist = hasSelectedStylists || isAllStaff;

  const handleAllStaff = useCallback(() => {
    L.debug('handleAllStaff()', isNotAllStaff);
    return void makeEveryStylist(isNotAllStaff);
  }, [makeEveryStylist, isNotAllStaff]);

  const handleTotalBusiness = useCallback(() => {
    deselectAllStylists();
    call('KpiSummaryStaffSelector.onTotalBusiness', onTotalBusiness);
  }, [deselectAllStylists, onTotalBusiness]);

  const handleOpen = useCallback(() => {
    toggleStaff();
    if (isOpen) return; // it was already open, so don't fire the onOpen

    // if isOpen was false, that means we're just now opening the list, so fire the event
    const event = { ids, isOpen: true, type: 'open', raw: stylists };
    call('KpiSummaryStaffSelector.onOpen', onOpen, event);
  }, [isOpen, onOpen, stylists, ids, toggleStaff]);

  const handleApply = useCallback(() => {
    const event = {
      ids,
      isOpen: false,
      type: 'apply',
      raw: stylists,
      isTotalBusiness: hasNoSelectedStylists,
      isMobile,
    };
    setPreviousStylists(stylists);
    call('KpiSummaryStaffSelector.onApply', onApply, event);
    call('KpiSummaryStaffSelector.onClose', onClose, event);
    closeStaff();
  }, [closeStaff, ids, isMobile, hasNoSelectedStylists, onApply, onClose, stylists]);

  const handleCancel = useCallback(() => {
    setStylists(previousStylists);
    // we'll just say the stylists are the same as before, that change will happen with setStylists as well
    const event = { ids, isOpen: false, type: 'cancel', raw: previousStylists };
    call('KpiSummaryStaffSelector.onCancel', onCancel, event);
    call('KpiSummaryStaffSelector.onClose', onClose, event);
    closeStaff();
  }, [setStylists, previousStylists, ids, onCancel, onClose, closeStaff]);

  useOnClickOutside(componentRef, handleCancel);

  const selectedName = selectedCount === 1 ? determineSelectedName({ ids, stylists: filteredStylists }) : '';

  const isApplyDisabled = !isMobile && hasNoSelectedStylists && isNotAllStaff && isSelected;
  const hasSelected = isAllStaff || hasSelectedStylists;
  const isActive = isOpen || (!isMobile && !isUnselected && hasSelected);

  L.debug('()', allStylistsCount, selectedCount, { stylists, activeStylists, stylistsFromProps });

  return (
    <div
      data-bem="KpiSummaryStaffSelector"
      className={cn({
        [CN.component]: true,
        [CN.higherZIndex]: isOpen,
        [CN.dark]: isDarkMode,
      })}
      ref={componentRef}
    >
      <Button
        tag="KpiSummaryStaffSelector__select-staff"
        height="medium"
        variant="quinary"
        active={isActive}
        onClick={handleOpen}
      >
        <div className={CN.buttonInner}>
          <div className={CN.buttonInnerText}>
            <div className={CN.displayDesktop}>{isUnselected && <Icon variant="arrowCursor" width="sm" />}</div>
            <div className={CN.displayTablet}>{isUnselected && <Icon variant="building" width="sm" />}</div>

            {isSelected && hasAnySelectedStylist && <Icon variant="userGroup" width="sm" />}
            {isSelected && !hasAnySelectedStylist && <Icon variant="arrowCursor" width="sm" />}

            <div data-bem="KpiSummaryStaffSelector__select-staff-members" className={CN.text}>
              <div data-bem="KpiSummaryStaffSelector__select-staff-members--desktop" className={CN.displayDesktop}>
                <div
                  className={cn({
                    [CN.bold]: hasAnySelectedStylist,
                  })}
                >
                  <TruncatedText
                    max={24}
                    suffix="..."
                    value={determineStaffButtonText({ selectedCount, selectedName, isAllStaff, isUnselected, t })}
                  />
                </div>
              </div>
              <div data-bem="KpiSummaryStaffSelector__select-staff-members--mobile" className={CN.displayTablet}>
                <div
                  className={cn({
                    [CN.bold]: hasAnySelectedStylist || isUnselected,
                  })}
                >
                  <TruncatedText
                    max={34}
                    suffix="..."
                    value={determineStaffButtonText({
                      selectedCount,
                      selectedName,
                      isAllStaff,
                      isUnselected,
                      isMobile: true,
                      t,
                    })}
                  />
                </div>
              </div>
            </div>
          </div>

          <Icon variant="arrowTriangle" width="xs" />
        </div>
      </Button>

      {isOpen && (
        <DropdownFoldout variant="withAll" width="maxInTabletP">
          <DropdownFoldout.Header>
            <div className={CN.selectedText}>{t('staffKpisReports.kpiReports.staffSelect.header')}</div>
          </DropdownFoldout.Header>
          <DropdownFoldout.Body>
            <div className={CN.dropdownContent}>
              <div className={CN.displayDesktop}>
                <div className={CN.searchContainer}>
                  <TextInput
                    variant="small"
                    value={searchText}
                    onChange={handleSearch}
                    placeholder={t('staffKpisReports.kpiReports.staffSelect.inputPlaceholder')}
                  />
                </div>
              </div>

              <div className={CN.displayTablet}>
                <div className={CN.gapBusinessContainer}>
                  <ToggleSwitch
                    tag="KpiSummaryStaffSelector__total-business"
                    name={t('staffKpisReports.kpiReports.totalBusiness')}
                    size="small"
                    variant="mobile"
                    selected={hasNoSelectedStylists}
                    onClick={handleTotalBusiness}
                  />
                </div>
              </div>

              <div data-bem="KpiSummaryStaffSelector__all-stylists" className={CN.itemSelectAll}>
                {fromAnyToBool(displaySelectAll) && (
                  <div className={CN.item}>
                    <TypedOption
                      tag="KpiSummaryStaffSelector__all-stylists"
                      type="simple-checkbox"
                      checked={isAllStaff}
                      onCheck={handleAllStaff}
                    >
                      <div className={CN.name}>{t('staffKpisReports.kpiReports.staffSelect.allStaffMembers')}</div>
                    </TypedOption>
                  </div>
                )}
              </div>

              {isLoading ? (
                <OverlaySpinner />
              ) : isNotFullArray(filteredStylists) ? (
                <p data-bem="KpiSummaryStaffSelector__no-stylists">{t('common.messages.noResults')}</p>
              ) : (
                filteredStylists.map((stylist) => {
                  const { userName: name, id } = stylist ?? {};

                  const checked = stylists[id] || false;
                  const onCheck = () => handleSelectStylist({ id, name });

                  return (
                    <div
                      data-bem="KpiSummaryStaffSelector__stylist"
                      data-stylist-id={id}
                      data-is-checked={checked}
                      key={id}
                      className={CN.item}
                    >
                      <TypedOption type="simple-checkbox" checked={checked} onCheck={onCheck}>
                        <div className={CN.name}>
                          <TruncatedText max={24} suffix="..." value={name} />
                        </div>
                      </TypedOption>
                    </div>
                  );
                })
              )}
            </div>
          </DropdownFoldout.Body>
          <DropdownFoldout.Footer>
            <div className={CN.dropdownFooterContainer}>
              <Button variant="outline" onClick={handleCancel}>
                <span className={CN.buttonGap}>{t('common.buttons.cancelUpp')}</span>
              </Button>
              <div className={CN.applyButtonContainer}>
                <Button variant="primaryForce" disabled={isApplyDisabled} onClick={handleApply}>
                  <span className={CN.buttonGap}>{t('common.buttons.applyUpp')}</span>
                </Button>
              </div>
            </div>
          </DropdownFoldout.Footer>
        </DropdownFoldout>
      )}
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default KpiSummaryStaffSelector;
